import type { ActedForView } from '@myrewards/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { actingForKeys, settleActedFor } from '../api/actingFor';
import { awardHistoryQuery, awardQuery, awardRecordQuery } from '../api/awards';
import { Button, ConfirmDialog, useToast } from '../ui';
import { actForProblemWords } from './words';

// APP-148 R92 (the decision's lines 132 and 306): a question that held nothing (a decline, a
// return, a withdrawal, an award that had moved on, a budget or setting change, Spot funding) is
// marked settled by whoever settles it: any Executive for what was done for an Executive, the
// award's Sector Lead for their own. A held question is the hold's (HeldCard); nothing is ever
// settled in Preview.

/**
 * Whether this row offers "Mark it settled" to this reader now: an open question that held
 * nothing, which the reader settles. A row sent only to its settler (R102) carries no question
 * words, but it's sent only while the question is open.
 */
export function marksSettled(view: ActedForView, previewing: boolean): boolean {
  return (
    !previewing &&
    (view.question !== null || view.can.read === false) &&
    view.settled === null &&
    !view.held &&
    view.can.settle
  );
}

/**
 * "Mark it settled" and its confirm: an optional note, then `settle` with `confirm` (the server
 * records it as resolved). A refusal is said in the server's words, in the dialog (R43).
 */
export function MarkSettled({
  view,
  meId,
}: {
  view: ActedForView;
  /** The reader: "your question" when it's theirs. */
  meId: string;
}): ReactElement {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The button names its row: the award's number, else what was done.
  const subject = view.award?.displayId ?? view.line;
  // N2: the confirm names the award where there is one, and never puts a whole line where an
  // award would go; without one, "About" is what it was (its facts), or nothing.
  const award = view.award === null ? null : (view.award.displayId ?? 'the award');
  const about = award ?? view.facts;
  const asker = view.question?.by ?? view.for;
  const theirs = asker.id === meId;
  const whose = theirs ? 'your' : `${asker.name}’s`;

  const settle = async (reason: string): Promise<boolean> => {
    const note = reason.trim();
    try {
      await settleActedFor(view.id, { outcome: 'confirm', ...(note !== '' && { note }) });
      setError(null);
      const award = view.award;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: actingForKeys.all }),
        // Waiting on you's rows, for everyone it went to.
        queryClient.invalidateQueries({ queryKey: ['home'] }),
        ...(award === null
          ? []
          : [
              queryClient.invalidateQueries({ queryKey: awardQuery(award.id).queryKey }),
              queryClient.invalidateQueries({ queryKey: awardRecordQuery(award.id).queryKey }),
              queryClient.invalidateQueries({ queryKey: awardHistoryQuery(award.id).queryKey }),
            ]),
      ]);
      toast.show({
        tone: 'done',
        title: 'Marked settled',
        body: theirs
          ? 'The Stock Admin team and the System Admins are told.'
          : `${asker.name}, the Stock Admin team and the System Admins are told.`,
      });
      return true;
    } catch (e) {
      setError(actForProblemWords(e));
      return false;
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Mark it settled ${subject}`}
        onPress={() => {
          setError(null);
          setOpen(true);
        }}
      >
        Mark it settled
      </Button>
      <ConfirmDialog
        isOpen={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) setError(null);
        }}
        title={`Mark ${whose} question settled?`}
        intro={`Nothing was held, so nothing changes${award === null ? '' : ` on ${award}`}: the question is marked settled, and ${theirs ? 'the Stock Admin team and the System Admins are' : `${asker.name}, the Stock Admin team and the System Admins are`} told.`}
        summary={[
          ...(about === null ? [] : [{ label: 'About', value: about }]),
          { label: 'Questioned by', value: theirs ? 'You' : asker.name },
        ]}
        reasonLabel="Note (optional)"
        reasonRequired={false}
        confirmLabel="Mark it settled"
        error={error}
        onConfirm={settle}
      />
    </>
  );
}
